import { useEffect, useState, useCallback } from "react";
import { optimizerApi, memoryApi } from "@/lib/api-client";
import { Brain, Sparkles, GitBranch, Clock } from "lucide-react";
import { relativeTime } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const TYPE_COLOR = {
  briefing: "#3B82F6",
  decision: "#F59E0B",
  outcome: "#22C55E",
  event: "#EF4444",
  proposal: "#A78BFA",
};

export default function IntelligenceCore({ refreshKey }) {
  const [proposals, setProposals] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [memory, setMemory] = useState([]);
  const [tab, setTab] = useState("optimizer");

  const load = useCallback(async () => {
    try {
      const [p, s, m] = await Promise.all([
        optimizerApi.proposals(), optimizerApi.signatures(), memoryApi.list(30)
      ]);
      setProposals(p); setSignatures(s); setMemory(m);
    } catch (e) { console.error("intelligence core load failed", e); }
  }, []);
  useEffect(() => { load(); }, [refreshKey, load]);

  return (
    <section className="uc-card p-6 uc-fade" data-testid="intelligence-core">
      <div className="mb-4">
        <div className="uc-chip mb-2">
          <Brain size={11} className="text-violet-400"/>
          INTELLIGENCE CORE
        </div>
        <h2 className="font-serif-display text-2xl">Self-Optimizer & Memory</h2>
        <p className="text-white/50 text-xs mt-1">Recurring threats become permanent fixes. Every decision is remembered.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b border-white/10 rounded-none p-0 h-auto justify-start gap-4 mb-4">
          <TabsTrigger data-testid="io-tab-optimizer" value="optimizer" className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-400 data-[state=active]:bg-transparent data-[state=active]:text-white text-white/50 px-0 pb-2 font-mono-uc text-[11px] uppercase tracking-widest">
            <Sparkles size={11} className="mr-1"/> Optimizer
          </TabsTrigger>
          <TabsTrigger data-testid="io-tab-signatures" value="signatures" className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-400 data-[state=active]:bg-transparent data-[state=active]:text-white text-white/50 px-0 pb-2 font-mono-uc text-[11px] uppercase tracking-widest">
            <GitBranch size={11} className="mr-1"/> Signatures
          </TabsTrigger>
          <TabsTrigger data-testid="io-tab-memory" value="memory" className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-400 data-[state=active]:bg-transparent data-[state=active]:text-white text-white/50 px-0 pb-2 font-mono-uc text-[11px] uppercase tracking-widest">
            <Clock size={11} className="mr-1"/> Memory
          </TabsTrigger>
        </TabsList>

        <TabsContent value="optimizer" className="mt-0 space-y-2 max-h-80 overflow-y-auto uc-scroll -mr-2 pr-2">
          {proposals.length === 0 && <Empty msg="No permanent-fix proposals yet. Repeated threats will trigger them."/>}
          {proposals.map((p) => (
            <div key={p.id} className="p-3 border border-violet-500/20 bg-violet-500/[0.03]" data-testid={`proposal-${p.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={11} className="text-violet-400"/>
                <span className="font-mono-uc text-[10px] uppercase tracking-widest text-violet-400">Auto-Proposal</span>
                <span className="font-mono-uc text-[10px] text-white/40">· {relativeTime(p.created_at)}</span>
              </div>
              <div className="text-sm text-white/90">{p.recurring_title}</div>
              <div className="font-mono-uc text-[10px] text-white/50 mt-1">
                Occurrences: {p.count_at_proposal}x · Status: {p.status}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="signatures" className="mt-0 space-y-1.5 max-h-80 overflow-y-auto uc-scroll -mr-2 pr-2">
          {signatures.length === 0 && <Empty msg="No recurring signatures tracked yet."/>}
          {signatures.map((s) => (
            <div key={s.id} className="p-2.5 border border-white/10 flex items-center justify-between gap-2" data-testid={`sig-${s.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white/85 truncate">{s.example_title}</div>
                <div className="font-mono-uc text-[9px] uppercase tracking-wider text-white/40">
                  {s.agent} · first {relativeTime(s.first_seen)}
                </div>
              </div>
              <div className={`font-mono-uc text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
                s.count >= 3 ? "border-red-500/50 text-red-400" : s.count >= 2 ? "border-amber-500/40 text-amber-400" : "border-white/15 text-white/60"
              }`}>
                {s.count}x
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="memory" className="mt-0 space-y-1.5 max-h-80 overflow-y-auto uc-scroll -mr-2 pr-2">
          {memory.length === 0 && <Empty msg="Long-term memory is empty — agents will start recording here."/>}
          {memory.map((m) => (
            <div key={m.id} className="p-2.5 border border-white/5 bg-black/40 flex items-start gap-2" data-testid={`mem-${m.id}`}
                 style={{ borderLeft: `2px solid ${TYPE_COLOR[m.type] || "#71717A"}` }}>
              <div className="min-w-0 flex-1">
                <div className="font-mono-uc text-[9px] uppercase tracking-widest" style={{ color: TYPE_COLOR[m.type] }}>
                  {m.type} {m.agent && <span className="text-white/40">· {m.agent}</span>}
                </div>
                <div className="text-[12px] text-white/80 mt-1 leading-relaxed">{m.content}</div>
                <div className="font-mono-uc text-[10px] text-white/40 mt-1">{relativeTime(m.created_at)}</div>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </section>
  );
}

function Empty({ msg }) {
  return <div className="text-white/40 text-xs italic py-6 text-center">{msg}</div>;
}
